/**
 * Login page @GestionalePD
 */
import React, { useState, useEffect } from 'react';
import {
  styled,
  IconButton,
  OutlinedInput,
  InputLabel,
  InputAdornment,
  Container,
  Box,
  Stack,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
// ------------------------------------------------------------
import { CustomTextField, CustomFormControl } from '../utils/StyledInputs';
import { DefaultContainedButton, DefaultOutlinedButton } from '../utils/Buttons';
import { PopupSuccessBody, PopupErrorBody, POPUP_DURATION_SUCCESS, POPUP_DURATION_ERROR } from '../utils/popUpStyles';
import { StyledLoginCard } from '../utils/StyledElements';
import { HttpErrorHandler } from '../utils/ErrorHandler';
import GlobalData from '../globalData';
import API from '../api';

// popup custom style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

/**
 * @returns Login Page UI
 */
export default function LoginPage() {
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const is900Screen = useMediaQuery('(min-width: 900px)');

  const [showPassword, setShowPassword] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [userLoginData, setUserLoginData] = useState({ username: '', password: '' });

  // Popup
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Show Popup message function
  const showPopup = (success, text) => {
    setPopupSuccess(success);
    setOpen(true);

    if (text) {
      setPopUpText(text);
    }

    setTimeout(
      () => {
        setOpen(false);
      },
      success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR
    );
  };

  // set local storage data
  useEffect(() => {
    if (
      GlobalData.loginData !== undefined &&
      GlobalData.userData !== undefined &&
      GlobalData.token !== undefined &&
      GlobalData.loginData !== null &&
      GlobalData.userData !== null &&
      GlobalData.token !== null
    ) {
      localStorage.setItem('loginData', JSON.stringify(GlobalData.loginData));
      localStorage.setItem('userData', JSON.stringify(GlobalData.userData));
      localStorage.setItem('token', GlobalData.token);
    }
  }, [GlobalData.loginData, GlobalData.userData, GlobalData.token]);

  // handle Login form data change
  const handleLoginFieldChange = (field, value) => {
    setUserLoginData((prevData) => ({ ...prevData, [field]: value }));
  };

  // handle password visualization function
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  // HTTP User Login request
  const onLogin = async (event) => {
    event.preventDefault();

    const loginData = {
      username: userLoginData.username.toUpperCase(),
      password: userLoginData.password,
    };

    // Login request
    API.post('/login', loginData)
      .then((response) => {
        const is2FaRequired = response.data['2fa_required'];

        if (!is2FaRequired) {
          if (response.status === 200) {
            const _responseUserData = response.data.user;
            const _responseUserAuthorization = response.data.authorisation;

            GlobalData.loginData = loginData;
            GlobalData.userData = _responseUserData;
            GlobalData.token = _responseUserAuthorization.token;
            setRedirect(true);
          }
        } else {
          handleOpen2faDialog();
          request2faCode();
        }
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const request2faCode = () => {
    API.post(`/requestCode?username=${encodeURIComponent(userLoginData.username)}`)
      .then((response) => {
        showPopup(true, response.data.message);
      })
      .catch((error) => {
        showPopup(false, error.response.data.message);
      });
  };

  // 2FA Dialog

  const [open2faDialog, setOpen2faDialog] = useState(false);
  const [code2fa, setCode2fa] = useState('');

  const handleOpen2faDialog = () => {
    setIsDialogOpen(true);
    setOpen2faDialog(true);
  };

  const handleClose2faDialog = () => {
    setIsDialogOpen(false);
    setOpen2faDialog(false);
  };

  const onVerify2faCode = () => {
    const loginData = {
      username: userLoginData.username,
      password: userLoginData.password,
      '2fa_code': code2fa,
    };

    API.post(`/confirmCode`, loginData)
      .then((response) => {
        if (response.status === 200) {
          const loginData = {
            username: userLoginData.username.toUpperCase(),
            password: userLoginData.password,
          };

          const _responseUserData = response.data.user;
          const _responseUserAuthorization = response.data.authorisation.token;

          GlobalData.loginData = loginData;
          GlobalData.userData = _responseUserData;
          GlobalData.token = _responseUserAuthorization;
          setRedirect(true);
        }
      })
      .catch((error) => {
        showPopup(false, error.response.data.error);
      });
  };

  // Redirect to modify password page
  if (redirect) {
    const lastUsedProfile = GlobalData.userData.roles.find((role) => role.last_used === 1);
    const pathname = lastUsedProfile.prof_type_name === 'USER' ? '/user/dashboard' : '/admin/dashboard';
    return <Navigate to={{ pathname }} />;
  }

  return (
    <div>
      <Helmet>
        <title>Partito Democratico</title>
      </Helmet>

      <Dialog open={open2faDialog} onClose={() => {}} fullWidth>
        <DialogTitle>Autenticazione a due fattori</DialogTitle>
        <DialogContent sx={{ marginTop: '18px' }}>
          <Typography sx={{ textAlign: 'center' }}>
            Inserisci il codice ricevuto via mail per completare il login.
          </Typography>

          <Stack direction="column" justifyContent="center" alignItems="center" gap="12px" sx={{ padding: '18px' }}>
            <CustomTextField
              customStyles={{ maxWidth: '200px' }}
              size="small"
              onChange={(e) => setCode2fa(e.currentTarget.value)}
              label="Inserisci il codice"
            />

            <Box sx={{ textAlign: 'center' }}>
              <Typography>Non hai ricevuto il codice?</Typography>

              <Typography
                onClick={() => request2faCode()}
                sx={{ textDecoration: 'underline', color: 'gray', cursor: 'pointer' }}
              >
                Invialo di nuovo
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <DefaultOutlinedButton onClick={handleClose2faDialog}>Chiudi</DefaultOutlinedButton>

          <DefaultContainedButton onClick={() => onVerify2faCode()}>Verifica</DefaultContainedButton>

          {isDialogOpen && (
            <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
              <Popup anchor={anchor} open={open}>
                {popupSuccess ? (
                  <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
                ) : (
                  <PopupErrorBody>{popUpText}</PopupErrorBody>
                )}
              </Popup>
            </Container>
          )}
        </DialogActions>
      </Dialog>

      <Stack direction="row" alignItems="center" width="100vw" height="100vh" sx={{ backgroundColor: '#ffffff' }}>
        <Box
          display={is900Screen ? 'block' : 'flex'}
          width={is900Screen ? 'calc(100vw - 12px)' : '100%'}
          height={is900Screen ? 'calc(100vh - 24px)' : '100%'}
          justifyContent={!is900Screen && 'center'}
          alignItems={!is900Screen && 'center'}
          className="default-bg"
        >
          <StyledLoginCard is900Screen={is900Screen} is600Screen={is600Screen}>
            <Box width="150px" sx={{ margin: '0 auto 24px' }}>
              <Box component="img" width="100%" src="assets/images/img/logopd.png" alt="" />
            </Box>

            <form action="post" onSubmit={onLogin}>
              <Stack direction="column" spacing={2}>
                <CustomTextField
                  id="username"
                  name="username"
                  onChange={(e) => handleLoginFieldChange('username', e.currentTarget.value)}
                  type="text"
                  label="Codice Fiscale o Email"
                  value={userLoginData.username}
                />

                <CustomFormControl>
                  <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
                  <OutlinedInput
                    id="outlined-adornment-password"
                    type={showPassword ? 'text' : 'password'}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseUp={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Password"
                    name="password"
                    onChange={(e) => handleLoginFieldChange('password', e.currentTarget.value)}
                    value={userLoginData.password}
                  />
                </CustomFormControl>
              </Stack>

              {/* Submit Button */}
              <Box>
                <DefaultContainedButton
                  customStyles={{
                    width: '100%',
                    height: '40px',
                    marginTop: '24px',
                  }}
                  type="submit"
                >
                  Accedi
                </DefaultContainedButton>

                {/* Popup style */}

                {!isDialogOpen && (
                  <Container ref={setAnchor}>
                    <Popup anchor={anchor} open={open}>
                      {popupSuccess ? (
                        <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
                      ) : (
                        <PopupErrorBody>{popUpText}</PopupErrorBody>
                      )}
                    </Popup>
                  </Container>
                )}
              </Box>

              {/* Login form Footer information */}
              <Stack
                direction={is600Screen ? 'row' : 'column'}
                spacing={2}
                justifyContent={is600Screen ? 'space-between' : 'start'}
                sx={{ paddingTop: 4 }}
              >
                <Link style={{ color: 'black', textDecoration: 'none' }} to={'/forgetPassword'}>
                  Password dimenticata?
                </Link>
                <Link style={{ color: 'black', textDecoration: 'none' }} to={'/join'}>
                  Iscriviti Ora
                </Link>
              </Stack>

              <Divider sx={{ color: 'text.secondary', margin: '36px 0' }} />
              <Stack>
                <DefaultOutlinedButton
                  onClick={() => {
                    window.location.href = 'https://www.partitodemocratico.it';
                  }}
                >
                  Homepage PD
                </DefaultOutlinedButton>
              </Stack>
            </form>
          </StyledLoginCard>
        </Box>
      </Stack>
    </div>
  );
}
